import { CommonModule, DecimalPipe } from '@angular/common';
import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import {
    DataSources,
    AppointmentsClient,
    AggregateType,
    InboundProductivityScoreExportQuery,
    InboundProductivityScoreQuery,
    IMyScoreShiftQuery,
    ITrendingQuery,
    InboundProductivityTrendingQuery,
    AccessType,
    PermissionType,
    AuthService,
} from '@services';
import {
    DxButtonComponent,
    DxDateRangeBoxComponent,
    DxSelectBoxComponent,
    DxTagBoxModule,
} from 'devextreme-angular';
import {
    ChartDataPoint,
    DoughnutChartComponent,
} from 'src/app/components/library/doughnut-chart/doughnut-chart.component';
import { TrendingComponent } from 'src/app/components/library/trending/trending.component';
import { CapitalCasePipe } from 'src/app/pipes/capital-case.pipe';
import { FileUtils } from 'src/app/shared/file-utils';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import EnumUtils from 'src/app/shared/enum-utils';

export function inboundProductivityPalette(): Record<string, string> {
    const getColor = (name: string) => getComputedStyle(document.body).getPropertyValue(name).trim();

    return {
        ["Day"]: getColor('--bs-success'),
        ["Afternoons"]: getColor('--bs-info'),
        ["WeekendDays"]: getColor('--bs-warning'),
        ["WeekendNights"]: getColor('--bs-danger'),
    };
}

@Component({
    selector: 'app-inbound-productivity-score',
    standalone: true,
    imports: [
        CommonModule,
        DoughnutChartComponent,
        DxDateRangeBoxComponent,
        DxButtonComponent,
        DxSelectBoxComponent,
        CapitalCasePipe,
        DecimalPipe,
        DxTagBoxModule,
        TrendingComponent,
    ],
    templateUrl: './inbound-productivity-score.component.html',
})
export class InboundProductivityScoreComponent implements OnInit {
    private appointmentsClient = inject(AppointmentsClient);
    private destroyRef = inject(DestroyRef);
    private dataSources = inject(DataSources);
    private auth = inject(AuthService);

    employeeIds: string[] = [];
    scoreData: ChartDataPoint[] = [];
    daysLookBack: number = 0;
    canManage: boolean = false;

    employeeDataSource = this.dataSources.inboundProductivityEmployeeOptions();

    dateRangeFilterValue: [Date, Date] = [
        new Date(new Date().setHours(0, 0, 0, 0)),
        new Date(new Date().setHours(0, 0, 0, 0)),
    ];

    trendingFilterOptions = EnumUtils.getEnumOptions(AggregateType);
    trendingFilterValue: AggregateType = AggregateType.Day;

    palette: Record<string, string> = {};

    ngOnInit(): void {
        this.initDateRange();

        this.canManage = this.auth.checkPermission(PermissionType.InboundProductivityScore, AccessType.Manage);

        if(!this.canManage) {
            this.employeeIds = this.auth.userId ? [this.auth.userId] : [];
        }

        this.palette = inboundProductivityPalette();
    }

    loadScoreData = (query: IMyScoreShiftQuery) => {
        return this.appointmentsClient.getInboundProductivityScore(new InboundProductivityScoreQuery({
            from: query.from!,
            to: query.to,
            employeeIds: this.selectedEmployeeIds,
        }));
    }

    loadTrendingData = (query: ITrendingQuery) => {
        return this.appointmentsClient.getInboundProductivityTrending(new InboundProductivityTrendingQuery({
            from: query.from,
            to: query.to,
            aggregateType: query.aggregateType,
            employeeIds: this.selectedEmployeeIds,
        }));
    }

    get employeeIdsKey(): string {
        return this.employeeIds.join(',');
    }

    private get selectedEmployeeIds(): string[] | undefined {
        return this.employeeIds.length ? this.employeeIds : undefined;
    }

    get fromDate(): Date {
        return this.dateRangeFilterValue?.[0];
    }

    get toDate(): Date {
        return this.dateRangeFilterValue?.[1];
    }

    onExport() {
        const query = new InboundProductivityScoreExportQuery({
            employeeIds: this.selectedEmployeeIds,
            from: this.fromDate,
            to: this.toDate,
        });

        this.appointmentsClient
            .exportInboundProductivity(query)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((res) => FileUtils.downloadFile(res));
    }

    onMainChartLoaded(items: ChartDataPoint[]) {
        this.scoreData = items;
    }

    private initDateRange(): void {
        if (this.daysLookBack > 0) {
            this.dateRangeFilterValue = [
                new Date(Date.now() - this.daysLookBack * 24 * 60 * 60 * 1000),
                new Date(),
            ];
        }
    }
}
